import { useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { CATEGORY_ORDER, CheckCategory } from '../../types/ReleaseCheckItem'

export type PanelKey = 'status' | 'all' | CheckCategory

const isCategory = (v: string | null): v is CheckCategory =>
  !!v && (CATEGORY_ORDER as string[]).includes(v)

const toPanel = (v: string | null): PanelKey =>
  v === 'all' || isCategory(v) ? v : 'status'

export interface PanelState {
  panel: PanelKey
  includeIgnored: boolean
  includeCompliant: boolean
  setPanel: (panel: PanelKey) => void
  setIncludeIgnored: (value: boolean) => void
  setIncludeCompliant: (value: boolean) => void
}

// URL is the single source of truth for the active panel + filters:
//   ?panel=KeyVault&ignored=1&compliant=1   (panel absent => status; flags absent => false)
export const usePanelState = (): PanelState => {
  const [searchParams, setSearchParams] = useSearchParams()

  const panel = toPanel(searchParams.get('panel'))
  const includeIgnored = searchParams.get('ignored') === '1'
  const includeCompliant = searchParams.get('compliant') === '1'

  const update = useCallback(
    (key: string, value: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (value === null) next.delete(key)
          else next.set(key, value)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  return {
    panel,
    includeIgnored,
    includeCompliant,
    setPanel: useCallback((p: PanelKey) => update('panel', p === 'status' ? null : p), [update]),
    setIncludeIgnored: useCallback((v: boolean) => update('ignored', v ? '1' : null), [update]),
    setIncludeCompliant: useCallback((v: boolean) => update('compliant', v ? '1' : null), [update]),
  }
}
